import type { Campaign } from "../api/generated";
import type {
  LandingPageLink,
  ModalStatus,
  OfferLandingPages,
  RebateProgramOption,
} from "./types";

export type ModalState = {
  selectedCampaign?: Campaign;
  selectedRebateProgram?: RebateProgramOption;
  selectedOffer?: OfferLandingPages;
  selectedLandingPage?: LandingPageLink;
  linkName: string;
  nameFieldStatus: "idle" | "duplicateName";
  status: ModalStatus;
  isTargetPageModalOpen: boolean;
  selectedLanguage: string;
};

export type ModalAction =
  | { type: "SELECT_REBATE_PROGRAM"; program?: RebateProgramOption }
  | { type: "SELECT_CAMPAIGN"; campaign: Campaign | null }
  | {
      type: "INIT_TARGET_PAGE";
      offer: OfferLandingPages;
      landingPage: LandingPageLink;
    }
  | {
      type: "CONFIRM_TARGET_PAGE";
      offer: OfferLandingPages;
      landingPage: LandingPageLink;
    }
  | { type: "SELECT_LANGUAGE"; landingPage: LandingPageLink }
  | { type: "SET_LINK_NAME"; name: string }
  | { type: "SET_STATUS"; status: ModalStatus }
  | { type: "OPEN_TARGET_PAGE_MODAL" }
  | { type: "CLOSE_TARGET_PAGE_MODAL" }
  | { type: "RESET"; defaultRebateProgram?: RebateProgramOption }
  | {
      type: "SET_NAME_FIELD_STATUS";
      nameFieldStatus: "idle" | "duplicateName";
    };

const buildDefaultLinkName = (pageName?: string, language?: string): string => {
  const safePageName = pageName?.trim() ?? "";
  const safeLanguage = language?.trim() ?? "";

  if (!safePageName && !safeLanguage) return "";
  if (!safePageName) return safeLanguage;
  if (!safeLanguage) return safePageName;

  const normalizedName = safePageName.toLowerCase();
  const normalizedLanguage = safeLanguage.toLowerCase();

  if (
    normalizedLanguage &&
    (normalizedName.endsWith(`- ${normalizedLanguage}`) ||
      normalizedName.endsWith(`-${normalizedLanguage}`))
  ) {
    return safePageName;
  }

  return `${safePageName} - ${safeLanguage}`;
};

export const createInitialState = (
  defaultRebateProgram?: RebateProgramOption,
): ModalState => ({
  selectedCampaign: undefined,
  selectedRebateProgram: defaultRebateProgram,
  selectedOffer: undefined,
  selectedLandingPage: undefined,
  selectedLanguage: "",
  linkName: "",
  nameFieldStatus: "idle",
  status: "idle",
  isTargetPageModalOpen: false,
});

export const modalReducer = (
  state: ModalState,
  action: ModalAction,
): ModalState => {
  switch (action.type) {
    case "SELECT_REBATE_PROGRAM":
      return {
        ...state,
        selectedRebateProgram: action.program,
        selectedOffer: undefined,
        selectedLandingPage: undefined,
        selectedLanguage: "",
        linkName: "",
        nameFieldStatus: "idle",
        status: "idle",
      };
    case "SELECT_CAMPAIGN":
      return {
        ...state,
        selectedCampaign: action.campaign ?? undefined,
        status: state.status === "duplicateName" ? "idle" : state.status,
      };
    case "INIT_TARGET_PAGE":
      if (state.selectedOffer && state.selectedLandingPage) return state;
      return {
        ...state,
        selectedOffer: action.offer,
        selectedLandingPage: action.landingPage,
        selectedLanguage: action.landingPage.languageCode,
        linkName: buildDefaultLinkName(
          action.landingPage.name,
          action.landingPage.languageCode,
        ),
      };
    case "CONFIRM_TARGET_PAGE":
      return {
        ...state,
        selectedOffer: action.offer,
        selectedLandingPage: action.landingPage,
        selectedLanguage: action.landingPage.languageCode,
        linkName: buildDefaultLinkName(
          action.landingPage.name,
          action.landingPage.languageCode,
        ),
        isTargetPageModalOpen: false,
        nameFieldStatus: "idle",
        status: "idle",
      };
    case "SELECT_LANGUAGE":
      if (!state.selectedOffer) return state;
      return {
        ...state,
        selectedLanguage: action.landingPage.languageCode,
        selectedLandingPage: action.landingPage,
        linkName: buildDefaultLinkName(
          action.landingPage.name,
          action.landingPage.languageCode,
        ),
        nameFieldStatus: "idle",
        status: "idle",
      };
    case "SET_LINK_NAME":
      return {
        ...state,
        linkName: action.name,
        nameFieldStatus: "idle",
        status: "idle",
      };
    case "SET_STATUS":
      return { ...state, status: action.status };
    case "SET_NAME_FIELD_STATUS":
      if (state.nameFieldStatus === action.nameFieldStatus) return state;
      return { ...state, nameFieldStatus: action.nameFieldStatus };
    case "OPEN_TARGET_PAGE_MODAL":
      return { ...state, isTargetPageModalOpen: true };
    case "CLOSE_TARGET_PAGE_MODAL":
      return { ...state, isTargetPageModalOpen: false };
    case "RESET":
      return createInitialState(action.defaultRebateProgram);
  }
};
